import { createFileRoute } from "@tanstack/react-router";

import {
  ServiceIntentPage,
  type ServiceFaq,
  type ServiceFinding,
  type ServiceSection,
} from "@/components/seo/ServiceIntentPage";
import { servicePageHead } from "@/lib/service-page-seo";

const PATH = "/meta-ads-audit";
const TITLE = "Senior Meta Ads Account Review | $950 Diagnostic | Adkiro";
const DESCRIPTION =
  "A $950 review of an existing Meta Ads account for property agencies, brokers and developers: a written diagnostic, prioritised 90-day roadmap and implementation handover by email.";

const SECTIONS: ServiceSection[] = [
  {
    eyebrow: "Campaign architecture",
    title: "Find where account structure hides the real signal",
    copy: "The audit reviews how objectives, campaigns, ad sets, audiences, budgets and exclusions work together. The goal is not a fashionable rebuild—it is a structure that makes the next decision clearer.",
    points: [
      "Review objectives, conversion locations, budgets and bid strategies",
      "Identify fragmentation, overlap and learning constraints",
      "Compare spend distribution with the client's commercial priorities",
      "Check that campaigns reaching the UK, Europe, the US or Canada are declared as housing ads",
      "Document immediate risks separately from longer-term experiments",
    ],
  },
  {
    eyebrow: "Creative and offer",
    title: "Separate fatigue, weak messaging and offer problems",
    copy: "Declining performance is not always an audience problem. We review the creative library, hooks, formats, proof, landing experience and historical winners to identify the next useful tests.",
    points: [
      "Review creative mix, frequency and performance by meaningful segments",
      "Map existing ads by hook, angle, proof and customer awareness",
      "Identify missing formats and evidence without inventing claims",
      "Check that ads for Dubai listings carry their Land Department permit and QR code",
      "Prioritise tests that can change a real campaign decision",
    ],
  },
  {
    eyebrow: "Measurement and plan",
    title: "Turn findings into a 90-day operating roadmap",
    copy: "Recommendations are ranked by urgency, effort and expected learning value. Tracking gaps are documented before historical platform reporting is treated as reliable evidence.",
    points: [
      "Check Pixel, Conversions API and lead form events, and their deduplication",
      "Review attribution settings and available source-of-truth reporting",
      "Deliver a prioritised 90-day testing and budget plan",
      "Deliver a written handover your team can act on directly",
    ],
  },
];

const STEPS = [
  {
    title: "Get the free review",
    copy: "A written reply within one business day says whether the $950 review is the right service for your account, before any access is requested.",
  },
  {
    title: "Pay and give view access",
    copy: "Pay online, then give Adkiro view access in Meta's settings: five steps on a computer, done by the admin of your Meta business account. Nothing in the account is changed.",
  },
  {
    title: "Receive the roadmap",
    copy: "Within three business days: the findings, proposed structure, 90-day priorities and a written handover, all by email.",
  },
];

const FINDINGS: ServiceFinding[] = [
  {
    title: "Every form counted as a buyer",
    copy: "The campaign optimises for any form submitted, so budget flows to people who tap Submit rather than to people who answer the phone.",
  },
  {
    title: "Housing rules applied after launch",
    copy: "Ads are rejected or restricted because Meta's housing category was added late, which resets learning and wastes the first weeks of budget.",
  },
  {
    title: "One ad for very different listings",
    copy: "The same creative runs across properties, prices and buyers, so nobody learns which location, price point or format brings enquiries.",
  },
];

const OWNERSHIP = [
  "The complete written account, creative and measurement review",
  "The prioritised 90-day roadmap, including risks, decisions and recommended tests",
  "The written handover, with answers in writing to the decisions the review raises",
];

const FAQS: ServiceFaq[] = [
  {
    q: "What is the difference between the free review and the $950 account review?",
    a: "The free review is a written reply to the form: which service fits and what it costs, without any access to your account. The $950 account review is the detailed work: campaign, creative and measurement analysis, a written diagnostic, prioritised 90-day roadmap and implementation handover, all delivered by email.",
  },
  {
    q: "What do you need from us for the review?",
    a: "For the free review, only the form: your website, budget and, if you like, what you advertise and a WhatsApp number for the reply. No account access is requested. For the $950 review, you give Adkiro access to view the ad account, as a partner in Meta's settings. The five steps are shown after payment; they are done on a computer by the admin of your Meta business account and can be forwarded to that person. If tracking needs a closer look, Kristian asks for that separately, in writing. Passwords are never requested, you keep ownership, and you can remove the access at any time.",
  },
  {
    q: "How long does the review take?",
    a: "The written review arrives by email within three business days of the access being granted. The free review before it is answered within one business day of your request.",
  },
  {
    q: "Do we need to meet or speak on a call?",
    a: "No. Adkiro works in writing. You receive a detailed written analysis that you keep, can review carefully with your team and apply at your own pace, and every recommendation and decision stays documented. Nobody has to find a time that suits Dubai, London and New York; questions are answered by email or WhatsApp.",
  },
  {
    q: "Will Adkiro change campaigns during the audit?",
    a: "Not without separate approval. The audit is designed to inspect and recommend. Any implementation, launch or ongoing management responsibility is confirmed before live account changes are made.",
  },
  {
    q: "Who is the account review for?",
    a: "Property agencies, brokers and developers already advertising on Meta who want a senior, independent view before changing budgets or before handing the account to Adkiro.",
  },
  {
    q: "How do I pay for the review?",
    a: "Online through Stripe, by card, Apple Pay or Google Pay, once Kristian has confirmed the review in writing. The payment button is on the Services & pricing page, and the link also comes with his reply.",
  },
  {
    q: "Do we keep the audit if we do not hire Adkiro afterward?",
    a: "Yes. The written review, prioritised 90-day roadmap and handover are yours to use internally or with another provider, subject to the agreed terms for confidential and third-party materials.",
  },
  {
    q: "Is the $950 review fee credited if we continue with Adkiro?",
    a: "Yes. The full $950 is credited against the first month of Adkiro monthly management if it follows the review.",
  },
];

export const Route = createFileRoute("/meta-ads-audit")({
  head: () =>
    servicePageHead({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Senior Meta Ads Account Review",
      serviceType: "Facebook and Instagram advertising account audit",
      audience: ["Property agencies and brokers", "Property developers"],
      faqs: FAQS,
    }),
  component: MetaAdsAuditPage,
});

function MetaAdsAuditPage() {
  return (
    <ServiceIntentPage
      badge="$950 one-time · for accounts already advertising"
      title="A senior review of the Meta account you already run"
      intro="A rigorous review of campaign structure, creative and measurement for property advertisers: a written diagnosis, a prioritised 90-day roadmap and a clear basis for what should follow."
      qualification="You keep ownership of every Meta asset. Adkiro works through controlled partner access and never asks for your Facebook password."
      sections={SECTIONS}
      steps={STEPS}
      faqs={FAQS}
      findings={FINDINGS}
      ownershipItems={OWNERSHIP}
      creditCopy="The full $950 is credited against the first month of Adkiro monthly management if it follows."
      primaryCtaHref="/#audit"
      ctaTitle="Start with a free review"
      ctaCopy="Kristian first checks whether the $950 review is the right service for your account, and replies in writing within one business day. Already sure it is the service you need? Pay on the Services & pricing page."
    />
  );
}
